/* =====================================================================
 * DMTeamContactList — 팀 멤버 연락처 목록 (1:1 대화 시작용)
 *
 * 팀별로 멤버를 그룹핑하여 표시합니다.
 * 클릭 시 find-or-create 패턴으로 1:1 대화를 열거나 생성합니다.
 * ===================================================================== */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Search, MessageCircle } from 'lucide-react';
import { useTeamStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useDMConversationStore } from '@auw/stores';
import { getTeamDetail, type TeamMember } from '@auw/api-client';
import { Input } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';

interface Props {
  onConversationOpen: (convId: string) => void;
  flatStyle?: boolean;
  surfaceOpacity?: number;
}

const DMTeamContactList: React.FC<Props> = ({
  onConversationOpen,
  flatStyle = false,
  surfaceOpacity = 1,
}) => {
  const { t } = useTranslation('common');
  const { teams } = useTeamStore();
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');
  const openDirect = useDMConversationStore((s) => s.openDirect);

  const [teamMembers, setTeamMembers] = useState<Record<string, TeamMember[]>>({});
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState<string | null>(null);
  const [avatarErrorIds, setAvatarErrorIds] = useState<Record<string, boolean>>({});

  // 팀별 멤버 로드
  useEffect(() => {
    const load = async () => {
      const map: Record<string, TeamMember[]> = {};
      for (const t of teams) {
        try {
          const detail = await getTeamDetail(t.team_id);
          map[t.team_id] = detail.members.filter((m) => m.user_id !== currentUserId);
        } catch {
          map[t.team_id] = [];
        }
      }
      setTeamMembers(map);
    };
    if (teams.length > 0) load();
  }, [teams, currentUserId]);

  // 필터링
  const filteredByTeam = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result: Record<string, TeamMember[]> = {};
    for (const [teamId, members] of Object.entries(teamMembers)) {
      const filtered = q
        ? members.filter(
            (m) =>
              (m.name || '').toLowerCase().includes(q) ||
              (m.email || '').toLowerCase().includes(q),
          )
        : members;
      if (filtered.length > 0) result[teamId] = filtered;
    }
    return result;
  }, [teamMembers, search]);

  const handleClick = async (teamId: string, member: TeamMember) => {
    setStarting(member.user_id);
    try {
      const convId = await openDirect(teamId, member.user_id);
      onConversationOpen(convId);
    } catch (err) {
      console.error('1:1 대화 생성 실패:', err);
    } finally {
      setStarting(null);
    }
  };

  const markAvatarError = (avatarKey: string) => {
    setAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  const teamNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of teams) m[t.team_id] = t.name;
    return m;
  }, [teams]);

  if (teams.length === 0) return null;

  return (
    <Container>
      <SearchBox $flatStyle={flatStyle} $surfaceOpacity={surfaceOpacity}>
        <Search size={14} />
        <Input
          variant="ghost"
          inputSize="sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('dm.searchTeamMembers')}
          style={{ flex: 1 }}
        />
      </SearchBox>

      {Object.entries(filteredByTeam).map(([teamId, members]) => (
        <Section key={teamId}>
          <SectionLabel>{teamNameMap[teamId] || teamId}</SectionLabel>
          {members.map((m) => {
            const avatarKey = `member:${m.user_id}:${m.picture || 'none'}`;
            const showAvatarImage = !!m.picture && !avatarErrorIds[avatarKey];
            return (
              <MemberRow
                key={m.user_id}
                onClick={() => handleClick(teamId, m)}
                $loading={starting === m.user_id}
              >
                <Avatar>
                  {showAvatarImage ? (
                    <AvatarImg
                      src={m.picture as string}
                      alt={m.name || m.user_id}
                      onError={() => markAvatarError(avatarKey)}
                    />
                  ) : (
                    (m.name || m.email || '?').charAt(0).toUpperCase()
                  )}
                </Avatar>
                <Info>
                  <MemberName>{m.name || m.email}</MemberName>
                  {m.name && m.email && <MemberEmail>{m.email}</MemberEmail>}
                </Info>
                <ChatIcon>
                  <MessageCircle size={14} />
                </ChatIcon>
              </MemberRow>
            );
          })}
        </Section>
      ))}

      {Object.keys(filteredByTeam).length === 0 && (
        <EmptyState description={t('dm.noTeamMembers')} />
      )}
    </Container>
  );
};

export default DMTeamContactList;

/* ---- Styled ---- */

const Container = styled.div`
  padding: 8px 0 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-top: 4px;
`;

const SearchBox = styled.div<{ $flatStyle: boolean; $surfaceOpacity: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  margin: 0 8px 4px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  border: ${({ $flatStyle, $surfaceOpacity, theme }) =>
    $flatStyle
      ? theme.mode === 'dark'
        ? `1px solid rgba(255,255,255,${Math.max(0.05, Math.min(0.12, $surfaceOpacity * 0.12))})`
        : `1px solid rgba(0,0,0,${Math.max(0.04, Math.min(0.10, $surfaceOpacity * 0.1))})`
      : 'none'};
  background: ${({ $flatStyle, $surfaceOpacity, theme }) =>
    $flatStyle
      ? theme.mode === 'dark'
        ? `rgba(255,255,255,${Math.max(0.03, Math.min(0.10, $surfaceOpacity * 0.10))})`
        : `rgba(255,255,255,${Math.max(0.08, Math.min(0.22, $surfaceOpacity * 0.22))})`
      : theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Section = styled.div`
  margin-bottom: 4px;
`;

const SectionLabel = styled.div`
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 8px 10px 4px;
`;

const MemberRow = styled.button<{ $loading: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberEmail = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatIcon = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  opacity: 0;
  ${MemberRow}:hover & { opacity: 1; }
`;

